<template>
  <div class="template-card" @click="handleChooseTemplate">
    <div v-if="$slots.thumbnail">
      <slot name="thumbnail" />
    </div>
    <div
      class="template-thumbnail"
      v-else
    >
      <div
        v-show="isThumbnailLoaded && hasThumbnail"
      >
        <img
          class="object-cover w-full h-full rounded"
          @load="onThumbnailLoad"
          v-if="presentation.customThumbnailImage"
          :src="presentation.customThumbnailImage"
          alt="thumbnail"
        >
        <img
          class="object-cover w-full h-full rounded"
          @load="onThumbnailLoad"
          v-if="presentation.thumbnailImage &&
            !presentation.customThumbnailImage"
          :src="presentation.thumbnailImage"
          alt="thumbnail"
        >
      </div>
      <aha-skeleton-loader
        v-show="!isThumbnailLoaded && hasThumbnail"
        width="100%"
        height="100%"
      />
      <!-- Can't wrap skeleton inside div, also can't use v-show and v-if same time,
      so condition will be like this... -->
      <img
        class="object-cover w-full h-full thumbnail-image"
        v-if="!hasThumbnail"
        :src="defaultThumbnail"
        alt="Thumbnail default"
      />
      <div class="template-thumbnail__hover"></div>
    </div>

    <p class="template-name" :title="presentation.name">
      {{ presentation.name }}
    </p>
    <TemplatePreviewPopup
      v-if="isShowingTemplatePreviewPopup"
      @hide="toggleTemplatePreviewPopup"
      :presentationShareCode="presentation.shareCode"
      :templateLocation="templateLocation"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Utils from '@/util'
import eventCategories from '@/constant/eventCategories'
import TemplatePreviewPopup from '@/components-v2/template/TemplatePreviewPopup.vue'

const defaultThumbnail = require('@/assets/images/icon/default-thumbnail.svg')

export default {
  components: {
    TemplatePreviewPopup,
  },
  props: {
    size: {
      type: String,
      default: 'md',
    },
    presentation: {
      type: Object,
      required: true,
    },
    customHandler: {
      type: Function,
      required: false,
      default: null,
    },
    templateLocation: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      defaultThumbnail,
      isThumbnailLoaded: false,
      isShowingTemplatePreviewPopup: false,
    }
  },
  computed: {
    ...mapGetters('presentationTemplates', [
      'copyingTemplateId',
    ]),
    hasThumbnail() {
      return this.presentation.thumbnailImage || this.presentation.customThumbnailImage
    },
  },
  methods: {
    ...mapActions('presentationTemplates', ['copyFromTemplateAction']),
    toggleTemplatePreviewPopup() {
      this.isShowingTemplatePreviewPopup = !this.isShowingTemplatePreviewPopup
      if (this.isShowingTemplatePreviewPopup) {
        document.body.classList.add('no-scroll-template')
      } else {
        document.body.classList.remove('no-scroll-template')
      }
    },
    handleCopyTemplateSuccess(presentation) {
      const newPresentationId = presentation.id
      const params = { id: newPresentationId }
      this.$router.push({ name: 'PresentationDetail', params })
      Utils.mixpanelTracking(eventCategories.TEMPLATE_LIBRARY, 'Create new presentation', 'Use template', null, {
        sourcePresentationId: this.presentation.id,
        presentationId: newPresentationId,
      })
    },
    handleChooseTemplate() {
      if (this.customHandler) {
        this.customHandler()
        return
      }
      this.toggleTemplatePreviewPopup()
    },
    goToLiveDemo() {
      this.$router.push({ name: 'PresentationPlayDemo', query: { id: this.presentation.id } })
      Utils.mixpanelTracking(eventCategories.TEMPLATE_LIBRARY, 'User clicks demo on template', this.presentation.id)
    },
    onThumbnailLoad() {
      this.isThumbnailLoaded = true
    },
  },
}
</script>

<style lang="scss" scoped>
  $card-width: 122px;
  $thumbnail-ratio: calc(16 / 9);

  .template-card {
    @apply rounded cursor-pointer;
    @apply hover:text-primary-90 transition-colors;
    max-width: $card-width;

    &:hover {
      .template-thumbnail {
        @apply shadow-card border-primary-90;
      }
      .template-thumbnail__hover {
        @apply opacity-100;
      }
    }

    .spinner { // align loader center
      width: auto;
    }

  }
  .template-thumbnail {
    @apply cursor-pointer relative mb-2 flex items-center justify-center rounded;
    width: $card-width;
    height: calc(#{$card-width} / #{$thumbnail-ratio});
  }

  .template-thumbnail__hover {
    @apply rounded absolute top-0 left-0 w-full h-full flex opacity-0;
    @apply justify-center items-center transition-opacity border border-primary-90;
    border-width: 2px;
  }

  .template-name {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-word;
    overflow: hidden;
    line-height: 1.125rem;
  }

</style>
